import { getTeamsSearch } from "@aura-frontend/omnibus-az-client";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import {
  Button,
  cn,
  Icons,
  Input,
  Label,
  useDebounce,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { CreateTeamRosterDialog } from "@/components/CreateTeamRosterDialog";
import { addOrgPrefix } from "@/lib/utils";
import { useManageLayoutContext } from "@/pages/Manage/ManageLayout";

import { RostersTable } from "./RostersTable/RostersTable";

const rostersInfiniteQueryOptions = (orgId: string, search?: string) =>
  infiniteQueryOptions({
    queryKey: ["getTeamsSearch", orgId, search],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getTeamsSearch({
        query: {
          continue: pageParam,
          ...(search && { name: search }),
          visibilityGroup: orgId,
        },
        signal,
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.continue,
    enabled: !!orgId,
  });

const Rosters = () => {
  const { orgId: contextOrgId } = useManageLayoutContext();
  const orgId = contextOrgId ? addOrgPrefix(contextOrgId) : "";

  const [rawSearchValue, setSearchValue] = useState("");
  const searchValue = useDebounce(rawSearchValue, 250, false);

  const [createRosterModalOpen, setCreateRosterModalOpen] = useState(false);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(rostersInfiniteQueryOptions(orgId, searchValue));

  const rosters = useMemo(
    () => data?.pages.flatMap((page) => page.teams) ?? [],
    [data?.pages],
  );

  return (
    <>
      <div className="relative flex flex-col">
        <div
          className={cn(
            "flex flex-col-reverse px-5 gap-4 items-start",
            "md:flex-row md:items-end md:justify-between",
          )}
        >
          <div>
            <Label htmlFor="roster-search" className="mb-2 text-sm">
              Search
            </Label>

            <Input
              placeholder="Look for a roster"
              className="w-60"
              name="roster-search"
              id="roster-search"
              inputMode="text"
              type="text"
              value={rawSearchValue}
              isClearable={true}
              onClear={() => {
                setSearchValue("");
              }}
              iconRight={
                <Icons.MagnifyingGlass className="text-placeholder h-5 w-5" />
              }
              onChange={(e) => {
                if (e.target.value === "") {
                  setSearchValue("");
                  return;
                }

                setSearchValue(e.target.value);
              }}
            />
          </div>

          <Button
            onClick={() => setCreateRosterModalOpen(true)}
            variant="secondary-flat-light"
            size="sm"
          >
            <Icons.Plus className="size-4" />
            Create New
          </Button>
        </div>

        <div className="mt-8">
          <RostersTable
            data={rosters}
            pageSize={40}
            isFetching={isLoading || isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            orgId={orgId}
          />
        </div>
      </div>

      {createRosterModalOpen && (
        <CreateTeamRosterDialog
          isOpen={createRosterModalOpen}
          onOpenChange={setCreateRosterModalOpen}
          mode="create"
          organizationId={orgId}
        />
      )}
    </>
  );
};

export { Rosters };
